import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, List, Map as MapIcon, X, Search, Loader2 } from "lucide-react";
import api from "../lib/api";
import { formatINR, CATEGORY_LABELS } from "../lib/format";
import PropertyCard from "../components/PropertyCard";
import MapView from "../components/MapView";

const CATEGORIES = ["residential", "plots", "commercial", "luxury", "investment"];
const BEDS = [1, 2, 3, 4, 5];
const SORTS = [["", "Relevance"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["area_desc", "Largest Area"], ["newest", "New Launches"]];
const PRICE_MAX = 100000000;
const FLAGS = [["verified", "Verified"], ["new_launch", "New Launch"], ["ready_to_move", "Ready to Move"], ["rera", "RERA Approved"]];

export default function SearchResults({ listing, category }) {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const [nl, setNl] = useState(params.get("q") || "");
  const [parsing, setParsing] = useState(false);
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price")) || PRICE_MAX);

  const query = useMemo(() => {
    const q = {};
    for (const [k, v] of params.entries()) q[k] = v;
    if (listing) q.listing_type = listing;
    if (category) q.category = category;
    return q;
  }, [params, listing, category]);

  useEffect(() => {
    setLoading(true);
    api.properties(query).then((d) => { setProperties(d); setLoading(false); }).catch(() => setLoading(false));
  }, [query]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value == null || value === false) next.delete(key);
    else next.set(key, value);
    setParams(next);
  };

  const toggleFlag = (key) => {
    const next = new URLSearchParams(params);
    if (next.get(key)) next.delete(key);
    else next.set(key, "true");
    setParams(next);
  };

  const clearAll = () => { setParams(new URLSearchParams()); setMaxPrice(PRICE_MAX); setNl(""); };

  const runNl = async (e) => {
    e.preventDefault();
    if (!nl.trim()) { clearAll(); return; }
    setParsing(true);
    try {
      const { filters } = await api.parseSearch(nl.trim());
      const next = new URLSearchParams();
      Object.entries(filters || {}).forEach(([k, v]) => { if (v !== "" && v != null) next.set(k, v); });
      setParams(next);
      setMaxPrice(Number(filters?.max_price) || PRICE_MAX);
    } catch { update("q", nl.trim()); }
    finally { setParsing(false); }
  };

  const activeCategory = query.category;
  const title = category ? CATEGORY_LABELS[category] : listing === "rent" ? "Properties for Rent" : listing === "buy" ? "Properties for Sale" : "All Properties";

  const FilterPanel = (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-pbs-dark">Filters</h3>
        <button onClick={clearAll} className="text-xs font-semibold text-pbs-coral">Clear All</button>
      </div>
      {!category && (
        <FilterGroup title="Category">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`filter-cat-${c}`} onClick={() => update("category", activeCategory === c ? "" : c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors capitalize ${activeCategory === c ? "bg-pbs-purple text-white border-pbs-purple" : "border-pbs-line text-pbs-grey hover:border-pbs-purple"}`}>
                {CATEGORY_LABELS[c]}
              </button>
            ))}
          </div>
        </FilterGroup>
      )}
      <FilterGroup title="Max Budget">
        <input type="range" min={2000000} max={PRICE_MAX} step={1000000} value={maxPrice}
          data-testid="filter-price-range"
          onChange={(e) => setMaxPrice(Number(e.target.value))}
          onMouseUp={(e) => update("max_price", Number(e.target.value) >= PRICE_MAX ? "" : e.target.value)}
          onTouchEnd={(e) => update("max_price", maxPrice >= PRICE_MAX ? "" : maxPrice)}
          className="w-full accent-pbs-purple" />
        <p className="text-sm font-bold text-pbs-dark mt-1">Up to {maxPrice >= PRICE_MAX ? "Any" : formatINR(maxPrice)}</p>
      </FilterGroup>
      <FilterGroup title="Bedrooms">
        <div className="flex flex-wrap gap-2">
          {BEDS.map((b) => (
            <button key={b} data-testid={`filter-beds-${b}`} onClick={() => update("bedrooms", Number(query.bedrooms) === b ? "" : b)}
              className={`w-11 h-9 rounded-lg text-sm font-semibold border transition-colors ${Number(query.bedrooms) === b ? "bg-pbs-purple text-white border-pbs-purple" : "border-pbs-line text-pbs-grey hover:border-pbs-purple"}`}>
              {b}+
            </button>
          ))}
        </div>
      </FilterGroup>
      <FilterGroup title="Property Status">
        <div className="space-y-2">
          {FLAGS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2.5 cursor-pointer text-sm text-pbs-dark">
              <input type="checkbox" data-testid={`filter-flag-${key}`} checked={!!query[key]} onChange={() => toggleFlag(key)} className="w-4 h-4 accent-pbs-purple" />
              {label}
            </label>
          ))}
        </div>
      </FilterGroup>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
      {/* NL search bar */}
      <form onSubmit={runNl} className="mb-5">
        <div className="flex items-center gap-2 bg-white rounded-xl p-1.5 border border-pbs-line pbs-shadow">
          <Search size={18} className="ml-2.5 text-pbs-purple shrink-0" />
          <input data-testid="results-search-input" value={nl} onChange={(e) => setNl(e.target.value)}
            placeholder='Search e.g. "3 BHK in Noida under ₹2 Cr"'
            className="flex-1 bg-transparent outline-none text-sm sm:text-base py-2 text-pbs-dark" />
          <button type="submit" data-testid="results-search-btn" disabled={parsing} className="flex items-center gap-1.5 pbs-gradient text-white font-bold text-sm px-4 py-2.5 rounded-lg">
            {parsing ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
          </button>
        </div>
      </form>

      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-pbs-dark">{title}</h1>
          <p className="text-sm text-pbs-grey mt-0.5" data-testid="results-count">{loading ? "Searching…" : `${properties.length} properties found`}</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="results-sort" value={query.sort || ""} onChange={(e) => update("sort", e.target.value)}
            className="bg-white border border-pbs-line rounded-lg px-3 py-2 text-sm font-semibold text-pbs-dark outline-none">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-btn" className="lg:hidden flex items-center gap-1.5 bg-white border border-pbs-line rounded-lg px-3 py-2 text-sm font-semibold">
            <SlidersHorizontal size={15} /> Filters
          </button>
          <div className="flex bg-white border border-pbs-line rounded-lg overflow-hidden">
            <button data-testid="view-list-btn" onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${view === "list" ? "bg-pbs-purple text-white" : "text-pbs-grey"}`}><List size={15} /> List</button>
            <button data-testid="view-map-btn" onClick={() => setView("map")} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold ${view === "map" ? "bg-pbs-purple text-white" : "text-pbs-grey"}`}><MapIcon size={15} /> Map</button>
          </div>
        </div>
      </div>

      <div className="flex gap-6">
        <aside className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-24 bg-white rounded-2xl border border-pbs-line p-5 pbs-shadow">{FilterPanel}</div>
        </aside>

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="py-20 text-center text-pbs-grey"><Loader2 className="animate-spin mx-auto mb-3" /> Loading…</div>
          ) : properties.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-lg font-bold text-pbs-dark">No properties match your filters</p>
              <button onClick={clearAll} className="mt-3 text-pbs-purple font-semibold">Clear filters</button>
            </div>
          ) : view === "map" ? (
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="h-[72vh] rounded-2xl overflow-hidden border border-pbs-line order-2 lg:order-1 overflow-y-auto no-scrollbar space-y-4 p-1">
                {properties.map((p) => <PropertyCard key={p.slug} property={p} compact />)}
              </div>
              <div className="h-[72vh] rounded-2xl overflow-hidden border border-pbs-line sticky top-24 order-1 lg:order-2" data-testid="map-container">
                <MapView properties={properties} />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {properties.map((p) => <PropertyCard key={p.slug} property={p} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white p-5 overflow-y-auto">
            <div className="flex justify-end mb-2"><button onClick={() => setShowFilters(false)}><X /></button></div>
            {FilterPanel}
            <button onClick={() => setShowFilters(false)} className="mt-6 w-full py-3 pbs-gradient text-white font-bold rounded-lg">Show {properties.length} results</button>
          </div>
        </div>
      )}
    </div>
  );
}

const FilterGroup = ({ title, children }) => (
  <div>
    <h4 className="text-sm font-bold text-pbs-dark mb-3">{title}</h4>
    {children}
  </div>
);
